import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { types as api } from '@mesa-labs/mesa-api';

import { EmptyVendorInvoiceFilterSet, VendorInvoiceFilterSet } from '../../utils/vendorInvoiceFilters';

export type VendorsState = {
  searchTerm?: string;
  instantPayoutAlwaysOn?: boolean;
  programCode?: api.ProgramCodes;
  onboardingPartnerId?: api.Partners;
  partnerIds?: readonly api.Partners[];
  statuses?: readonly api.VendorStatus[];
  collectionSchemes?: readonly api.InvoiceCollectionScheme[];
  currentVendorId?: api.UUID;
  invoiceSearchTerm?: string;
  // Vendor Invoices page filters other than search, which its search box owns
  invoiceFilters?: VendorInvoiceFilterSet;
  auditEntityTable?: api.VendorAuditEntityTable;
};

export const initialState: VendorsState = {
  searchTerm: '',
  instantPayoutAlwaysOn: undefined,
  programCode: undefined,
  onboardingPartnerId: undefined,
  partnerIds: undefined,
  statuses: undefined,
  collectionSchemes: undefined,
  currentVendorId: undefined,
  invoiceSearchTerm: '',
  invoiceFilters: EmptyVendorInvoiceFilterSet,
  auditEntityTable: undefined,
};

const vendorsSlice = createSlice({
  name: 'vendors',
  initialState,
  reducers: {
    updateSearchTerm(state: VendorsState, action: PayloadAction<string>) {
      state.searchTerm = action.payload;
    },
    updateInstantPayoutAlwaysOn(state: VendorsState, action: PayloadAction<boolean | undefined>) {
      state.instantPayoutAlwaysOn = action.payload;
    },
    updateProgramCode(state: VendorsState, action: PayloadAction<api.ProgramCodes | undefined>) {
      state.programCode = action.payload;
    },
    updateOnboardingPartnerId(state: VendorsState, action: PayloadAction<api.Partners | undefined>) {
      state.onboardingPartnerId = action.payload;
    },
    // A bare string is accepted since useSyncQueryStringParamsToRedux dispatches the raw query-string value
    updatePartnerIds(state: VendorsState, action: PayloadAction<readonly api.Partners[] | api.Partners | string | undefined>) {
      state.partnerIds = action.payload === undefined ? undefined : [action.payload].flat().map((p) => Number(p));
    },
    // A bare value is accepted since useSyncQueryStringParamsToRedux dispatches the raw query-string value
    updateStatuses(state: VendorsState, action: PayloadAction<readonly api.VendorStatus[] | api.VendorStatus | undefined>) {
      state.statuses = typeof action.payload === 'string' ? [action.payload] : action.payload;
    },
    updateCollectionSchemes(state: VendorsState, action: PayloadAction<readonly api.InvoiceCollectionScheme[] | api.InvoiceCollectionScheme | undefined>) {
      state.collectionSchemes = typeof action.payload === 'string' ? [action.payload] : action.payload;
    },
    updateCurrentVendorId(state: VendorsState, action: PayloadAction<api.UUID>) {
      if (state.currentVendorId !== action.payload) {
        state.invoiceSearchTerm = '';
        state.invoiceFilters = EmptyVendorInvoiceFilterSet;
        state.currentVendorId = action.payload;
      }
    },
    updateInvoiceSearchTerm(state: VendorsState, action: PayloadAction<string>) {
      state.invoiceSearchTerm = action.payload;
    },
    updateInvoiceFilters(state: VendorsState, action: PayloadAction<VendorInvoiceFilterSet>) {
      state.invoiceFilters = action.payload;
    },
    updateAuditEntityTable(state: VendorsState, action: PayloadAction<api.VendorAuditEntityTable | undefined>) {
      state.auditEntityTable = action.payload;
    },
  },
});

export const {
  updateSearchTerm,
  updateInstantPayoutAlwaysOn,
  updateProgramCode,
  updateOnboardingPartnerId,
  updatePartnerIds,
  updateStatuses,
  updateCollectionSchemes,
  updateCurrentVendorId,
  updateInvoiceSearchTerm,
  updateInvoiceFilters,
  updateAuditEntityTable,
} = vendorsSlice.actions;
export default vendorsSlice.reducer;
